import { Box, colors, Dialog, DialogContent, DialogContentText, Tooltip } from '@mui/material';
import { get } from 'lodash';
import { FC, useCallback, useState } from 'react';
import {
  Confirm,
  useChoices,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { EmailBlockLogResource, PostRemoveEmailBlockEndpoint } from '@Common/types/bo/users/users';

import { DialogTitleWithX } from '@sh4res/bo-common/components';
import {
  emailStateChoices,
  emailStateTooltipChoices,
} from '@sh4res/bo-common/components/B2bEmployee/choices';
import { SelectChipField } from '@sh4res/bo-common/fields';
import { useCanAccess, useCreatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

const highlightSx = {
  '& .MuiTypography-root': {
    color: colors.red[500],
  },
  '& .MuiChip-root': {
    color: colors.red[500],
    backgroundColor: colors.red[50],
  },
  '& .MuiChip-root.clickable': {
    cursor: 'pointer',
  },
  '& .MuiChip-root.clickable:hover': {
    color: colors.red[700],
    backgroundColor: colors.red[100],
  },
};

type EmailChipProps = {
  source?: string;
  emailSource?: string;
  testId?: string;
  // Defaults to the record context id
  logResource?: EmailBlockLogResource;
  logResourceId?: number;
};

const unremovableBlockReasons = {
  permanently_blocked:
    'resources.bo_common.users.actions.unremovable_block.reason.permanently_blocked',
  invalid: 'resources.bo_common.users.actions.unremovable_block.reason.invalid',
} as const;

const isUnremovableBlock = (value: unknown): value is keyof typeof unremovableBlockReasons =>
  typeof value === 'string' && value in unremovableBlockReasons;

export const EmailStateChip: FC<EmailChipProps> = (props) => {
  const { source, emailSource, testId, logResource, logResourceId } = props;
  const record = useRecordContext();
  const { getChoiceText, getChoiceValue } = useChoices({});

  const [removeBlockConfirmOpen, setRemoveBlockConfirmOpen] = useState<boolean>(false);
  const openRemoveBlockConfirmModal = useCallback(() => setRemoveBlockConfirmOpen(true), []);
  const closeRemoveBlockConfirmModal = useCallback(() => setRemoveBlockConfirmOpen(false), []);

  const [unremovableBlockInfoOpen, setUnremovableBlockInfoOpen] = useState<boolean>(false);
  const openUnremovableBlockInfo = useCallback(() => setUnremovableBlockInfoOpen(true), []);
  const closeUnremovableBlockInfo = useCallback(() => setUnremovableBlockInfoOpen(false), []);

  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();

  const [removeBlock] = useCreatePromise<PostRemoveEmailBlockEndpoint['Client']['RequestBody']>();

  const allowUnlock = useCanAccess({ action: 'create', resource: 'users/email_block' });

  const handleRemoveBlock = useCallback(async () => {
    if (source === undefined || emailSource === undefined || !allowUnlock) {
      return;
    }
    const state = get(record, source);
    const email = get(record, emailSource);
    if (state !== 'blocked' || email === undefined) {
      return;
    }

    const resourceId = logResourceId ?? record?.id;
    const logResourcePayload =
      logResource !== undefined && typeof resourceId === 'number'
        ? { resource: logResource, id: resourceId }
        : undefined;

    try {
      await removeBlock('users/email_block', {
        data: { email, log_resource: logResourcePayload },
      });
      notify('resources.bo_common.users.actions.remove_block.success', {
        type: 'success',
      });
      setRemoveBlockConfirmOpen(false);
      refresh();
    } catch (error: unknown) {
      notify('resources.bo_common.users.actions.remove_block.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    }
  }, [
    record,
    source,
    emailSource,
    logResource,
    logResourceId,
    allowUnlock,
    notify,
    refresh,
    removeBlock,
  ]);

  if (source === undefined) {
    return null;
  }

  const value = get(record, source);
  const choice = emailStateTooltipChoices.find((choice) => getChoiceValue(choice) === value);
  const choiceText = choice ? getChoiceText(choice) : null;

  if (!value) {
    return null;
  }

  const canRemoveBlock = allowUnlock && value === 'blocked' && emailSource !== undefined;
  const hasUnremovableBlock = isUnremovableBlock(value);
  let handleChipClick: (() => void) | undefined;
  if (canRemoveBlock) {
    handleChipClick = openRemoveBlockConfirmModal;
  } else if (hasUnremovableBlock) {
    handleChipClick = openUnremovableBlockInfo;
  }

  return (
    <Box display="flex" flexDirection="row" sx={highlightSx}>
      {choiceText === null ? (
        <SelectChipField data-testid={testId} source={source} choices={emailStateChoices} />
      ) : (
        <Tooltip title={choiceText} arrow>
          <span style={{ display: 'inline-flex' }}>
            <SelectChipField
              data-testid={testId}
              source={source}
              choices={emailStateChoices}
              className={handleChipClick ? 'clickable' : undefined}
              onClick={handleChipClick}
            />
          </span>
        </Tooltip>
      )}
      {canRemoveBlock && (
        <Confirm
          isOpen={removeBlockConfirmOpen}
          title="resources.bo_common.users.actions.remove_block.title"
          content={
            <>
              <DialogContentText>
                {translate('resources.bo_common.users.actions.remove_block.confirm')}
              </DialogContentText>
              <DialogContentText variant="body2">
                {translate('resources.bo_common.users.actions.remove_block.confirm_note')}
              </DialogContentText>
            </>
          }
          onConfirm={handleRemoveBlock}
          onClose={closeRemoveBlockConfirmModal}
        />
      )}
      {hasUnremovableBlock && (
        <Dialog
          open={unremovableBlockInfoOpen}
          onClose={closeUnremovableBlockInfo}
          fullWidth
          maxWidth="sm">
          <DialogTitleWithX onClose={closeUnremovableBlockInfo}>
            {translate('resources.bo_common.users.actions.unremovable_block.title')}
          </DialogTitleWithX>
          <DialogContent>
            <DialogContentText gutterBottom>
              {translate(unremovableBlockReasons[value])}
            </DialogContentText>
            <DialogContentText>
              {translate('resources.bo_common.users.actions.unremovable_block.resolution')}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};
